import { ArcsTokenIcon } from '@components/ui/ArcsTokenIcon'
import { IconButton } from '@components/ui/IconButton'
import { AnimatePresence, motion } from 'framer-motion'
import type React from 'react'
import ethereumTokenIcon from '../../../public/icons/EthereumTokenIcon.svg'

export type TokenType = 'arcs' | 'ethereum'

interface TokenOption {
  type: TokenType
  name: string
  symbol: string
}

const TOKEN_OPTIONS: TokenOption[] = [
  { type: 'arcs', name: 'ARCS', symbol: 'ARX' },
  { type: 'ethereum', name: 'Ethereum', symbol: 'ETH' },
]

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="#3B82F6" />
    <path
      d="M8 12L11 15L16 9"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

interface TokenSelectSheetProps {
  open: boolean
  title: string
  description?: string
  selectedToken: TokenType
  onSelect: (token: TokenType) => void
  onClose: () => void
}

export const TokenSelectSheet: React.FC<TokenSelectSheetProps> = ({
  open,
  title,
  description,
  selectedToken,
  onSelect,
  onClose,
}) => {
  const handleSelect = (token: TokenType) => {
    onSelect(token)
    onClose()
  }

  return (
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          key="token-select-sheet"
          className="fixed inset-0 z-50 flex w-full justify-center"
          aria-hidden={!open}
          style={{ pointerEvents: open ? 'auto' : 'none' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {/* 背景 */}
          <motion.button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="閉じる"
            onClick={onClose}
            tabIndex={open ? 0 : -1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          />

          {/* シート */}
          <motion.dialog
            className="relative mt-auto mb-0 flex w-full max-w-[480px] justify-center border-0 bg-transparent px-0"
            open
            aria-labelledby="token-select-title"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 420, damping: 32, mass: 0.8 }}
          >
            <div className="flex w-full flex-col rounded-t-[32px] bg-white pb-10 pt-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]">
              {/* ドラッグハンドル */}
              <div className="flex justify-center">
                <div className="h-1.5 w-12 rounded-full bg-zinc-300" aria-hidden />
              </div>

              {/* ヘッダー */}
              <header className="mt-3 flex items-center justify-between px-6">
                <div className="inline-flex h-10 w-10 items-center justify-center" />
                <p
                  id="token-select-title"
                  className="text-[20px] font-bold leading-none text-zinc-900"
                >
                  {title}
                </p>
                <IconButton
                  aria-label="閉じる"
                  icon="Close"
                  onClick={onClose}
                  tabIndex={open ? 0 : -1}
                />
              </header>

              {/* メインコンテンツ */}
              <div className="mt-6 flex flex-col gap-4 px-6">
                {description && (
                  <p className="text-[14px] font-medium text-zinc-500">{description}</p>
                )}

                {/* トークンオプション */}
                <div className="flex flex-col gap-3">
                  {TOKEN_OPTIONS.map((option) => {
                    const isSelected = selectedToken === option.type

                    return (
                      <button
                        key={option.type}
                        type="button"
                        onClick={() => handleSelect(option.type)}
                        className={`flex w-full items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 ${
                          isSelected ? 'border-blue-200 bg-blue-50/50' : 'border-zinc-100'
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          {option.type === 'arcs' ? (
                            <ArcsTokenIcon className="size-10" />
                          ) : (
                            <img
                              src={ethereumTokenIcon}
                              alt=""
                              aria-hidden="true"
                              className="size-10"
                            />
                          )}
                          <span className="flex flex-col">
                            <span className="text-[16px] font-semibold text-zinc-900">
                              {option.name}
                            </span>
                          </span>
                        </span>
                        {isSelected && <CheckIcon className="size-6" />}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </motion.dialog>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export default TokenSelectSheet
